import { Injectable, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ApiService } from '../api/api.service';

/**
 * Listing-owner contact details for the "Message by WhatsApp / Email" action.
 *
 * TWO ENDPOINTS, ONE SHAPE:
 * - `/Public/properties/:id/contact` — no session needed, published units only.
 *   This is what makes the button work for a visitor with no account, which is
 *   the whole point: `wa.me/<number>` IS the number, so there is no popup that
 *   could stand between the click and WhatsApp.
 * - `/API2/properties/:id/contact` — signed-in, and the only one that can see
 *   inventory the public view hides (drafts, unpublished units an org previews).
 *
 * Both return `{ vendorEmail, vendorPhone }`, so callers never branch on which
 * one answered.
 */
export interface PropertyOwnerContact {
    vendorEmail: string | null;
    vendorPhone: string | null;
}

const EMPTY_CONTACT: PropertyOwnerContact = { vendorEmail: null, vendorPhone: null };

/**
 * WhatsApp wants digits only — no `+`, spaces or punctuation. Anything outside
 * 7–15 digits is not a dialable international number (E.164's own bounds), and a
 * bad `wa.me` link lands the visitor on an error page instead of a chat, so it
 * is better treated as "no WhatsApp" than handed over.
 */
export function whatsappDigits(value: unknown): string | null {
    const raw = String(value ?? '').trim();
    if (!raw) return null;
    const digits = raw.replace(/[^\d]/g, '');
    if (!digits) return null;
    if (digits.length < 7 || digits.length > 15) return null;
    return digits;
}

export function emailAddressOrNull(value: unknown): string | null {
    const raw = String(value ?? '').trim();
    return raw && raw.includes('@') ? raw : null;
}

export function whatsappLinkFor(phone: string | null, text: string): string {
    const body = encodeURIComponent(text || '');
    return phone ? `https://wa.me/${phone}?text=${body}` : `https://wa.me/?text=${body}`;
}

export function mailtoLinkFor(email: string | null, subject: string, body: string): string {
    const to = encodeURIComponent(email || '');
    return `mailto:${to}?subject=${encodeURIComponent(subject || '')}&body=${encodeURIComponent(body || '')}`;
}

@Injectable({ providedIn: 'root' })
export class PropertyContactService {
    private api = inject(ApiService);

    getPublicContact(propertyId: string): Observable<PropertyOwnerContact> {
        const id = String(propertyId || '').trim();
        if (!id) return of(EMPTY_CONTACT);
        return this.api
            .get<any>(`/Public/properties/${encodeURIComponent(id)}/contact`)
            .pipe(map((resp) => this.normalize(resp)));
    }

    getPrivateContact(propertyId: string): Observable<PropertyOwnerContact> {
        const id = String(propertyId || '').trim();
        if (!id) return of(EMPTY_CONTACT);
        return this.api
            .get<any>(`/API2/properties/${encodeURIComponent(id)}/contact`)
            .pipe(map((resp) => this.normalize(resp)));
    }

    /**
     * Signed-in callers go through API2 so private inventory still resolves, and
     * fall back to the public route when API2 refuses (an expired session that
     * survived the refresh attempt, or an actor type API2 rejects). Guests only
     * ever have the public route, so a failure there is a real failure.
     */
    resolveContact(propertyId: string, options: { authenticated: boolean }): Observable<PropertyOwnerContact> {
        const id = String(propertyId || '').trim();
        if (!id) return of(EMPTY_CONTACT);
        if (!options?.authenticated) return this.getPublicContact(id);

        return this.getPrivateContact(id).pipe(
            catchError((err) => {
                const status = Number(err?.status ?? err?.error?.status ?? NaN);
                if (status === 401 || status === 403) return this.getPublicContact(id);
                throw err;
            })
        );
    }

    private normalize(resp: any): PropertyOwnerContact {
        const row = resp?.data && typeof resp.data === 'object' ? resp.data : resp;
        return {
            vendorEmail: emailAddressOrNull(row?.vendorEmail ?? row?.vendor_email),
            vendorPhone: String(row?.vendorPhone ?? row?.vendor_phone ?? '').trim() || null
        };
    }
}
